/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import {
  childrenSelector,
  Dispatch,
  labelSelector,
  readonlySelector,
  titleSelector,
} from '@smartsheet-dataflow/formjs';
import {
  connect,
  EntityId,
  EntityProvider,
} from '@smartsheet-dataflow/formjs-react';
import {
  Button,
  colors,
  sizes,
  Spacer,
  TypeRampV2,
} from '@smartsheet/lodestar-core';
import { RemoveIcon, ToolbarChevronUpIcon } from '@smartsheet/lodestar-icons';
import React, { useState } from 'react';
import useCollapse from 'react-collapsed';
import { BridgeFormJSState } from '../../services/forms/Types';
import { isOpenSelector, setIsOpenAction } from '../../services/forms/features';

interface InputGroupProps extends EntityId {
  label?: string;
  entities?: string[];
  isOpen?: boolean;
  onRemove?: (inputId: string) => void;
}

export const InputGroup = ({
  id,
  label,
  entities,
  isOpen = false,
  onRemove,
}: InputGroupProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(isOpen);
  const { getToggleProps, getCollapseProps } = useCollapse({
    isExpanded,
  });

  const style = css`
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    :hover,
    :focus {
      .chevron-container {
        background-color: ${colors.focusLight20};
      }
    }
  `;

  const labelStyle = css`
    line-height: ${sizes.xLarge}px !important;
    width: calc(100% - ${sizes.xLarge}px);
  `;

  const chevronContainerStyle = css`
    border-radius: 2px;
    box-sizing: border-box;
    height: ${sizes.xLarge}px;
    width: ${sizes.xLarge}px;
    padding: calc(11px / 2);
  `;

  const chevronStyle = css`
    transition: transform 200ms;
    ${isExpanded ? 'transform: rotate(180deg)' : 'transform: rotate(90deg)'}
  `;

  if (id !== 'form' && label !== undefined && label !== '') {
    return (
      <React.Fragment>
        <div
          css={style}
          {...getToggleProps({
            onClick: () => {
              setIsExpanded(!isExpanded);
            },
          })}
          id={id}
        >
          <TypeRampV2
            system="lodestar"
            variant={'headerSmall'}
            css={labelStyle}
          >
            {label}
          </TypeRampV2>
          <div className="chevron-container" css={chevronContainerStyle}>
            <ToolbarChevronUpIcon css={chevronStyle} />
          </div>
          {onRemove ? (
            <Button
              appearance="secondary-destructive"
              size="medium"
              css={css`
                margin-left: ${sizes.small}px !important;
              `}
              iconBefore={<RemoveIcon />}
              onClick={() => onRemove(id)}
            />
          ) : null}
        </div>
        <Spacer orientation="column" space="large" {...getCollapseProps()}>
          {Array.isArray(entities) &&
            entities.map((childId: string) => (
              <EntityProvider key={childId} id={childId} />
            ))}
        </Spacer>
      </React.Fragment>
    );
  } else {
    return (
      <Spacer id={id} orientation="column" space="large">
        {Array.isArray(entities) &&
          entities.map((childId: string) => (
            <EntityProvider key={childId} id={childId} />
          ))}
      </Spacer>
    );
  }
};

const mapState = (state: BridgeFormJSState, props: InputGroupProps) => {
  const { id, label } = props;
  return {
    label: label ? label : labelSelector(id)(state),
    title: titleSelector(id)(state),
    readonly: readonlySelector(id)(state),
    entities: childrenSelector(id)(state),
    isOpen: isOpenSelector(id)(state),
  };
};

const mapDispatch = (dispatch: Dispatch, { id }: EntityId) => {
  const setIsOpen = setIsOpenAction(dispatch);
  return {
    setIsOpen: (isOpen: boolean) => {
      setIsOpen({ inputId: id, isOpen });
    },
  };
};

export const FormJSInputGroup = connect(mapState, mapDispatch)(InputGroup);
